import React, { useMemo, useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  makeStyles,
} from '@material-ui/core';
import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-material-ui';
import usersService from '../../services/users';
import { RouteComponentProps, withRouter } from 'react-router';
import { useSnackbar } from 'notistack';
import SelectRole from '../Util/SelectRole';

type TParams = { account: string };

interface Props extends RouteComponentProps<TParams> {
  user?: any;
  onClose: (reload: Boolean) => void;
}

interface Values {
  email: string;
  password: string;
}

const useStyles = makeStyles({
  paper: {
    overflow: 'visible',
  },
});

const CreateEditUser: React.FunctionComponent<Props> = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [roles, setRoles] = useState<Array<string>>(props.user?.roles);

  const isNew = useMemo(() => {
    return props.user && props.user.id === '-1';
  }, [props.user]);

  const onrolesChange = useCallback((newroles) => {
    setRoles(newroles);
  }, []);

  const classes = useStyles();

  return (
    <Formik
      initialValues={{
        email: props.user?.email || '',
        firstname: props.user?.firstName || '',
        lastname: props.user?.lastName || '',
        account: props.match.params.account,
        roles: props.user?.roles,
        admin: false,
      }}
      validate={(values) => {
        const errors: Partial<Values> = {};
        if (!values.email) {
          errors.email = 'Required';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
          errors.email = 'Invalid email address';
        }
        return errors;
      }}
      onSubmit={(values, { setSubmitting }) => {
        values = { ...values, roles: roles };
        if (isNew) {
          usersService.createUser(values).then(
            (data) => {
              setSubmitting(false);
              if (!data.data?.createUser?.error) {
                props.onClose(true);
              } else {
                enqueueSnackbar('Failed to add user: ' + data.data.createUser.error, {
                  variant: 'error',
                  autoHideDuration: 3000,
                });
              }
            },
            () => {
              setSubmitting(false);
              enqueueSnackbar('Failed to invite user', { variant: 'error', autoHideDuration: 3000 });
            }
          );
        } else if (props.user) {
          usersService.updateUser(
            props.user.id,
            values).then(
            () => {
              setSubmitting(false);
              props.onClose(true);
            },
            () => {
              setSubmitting(false);
              enqueueSnackbar('Failed to update user', { variant: 'error', autoHideDuration: 3000 });
            }
          );
        }
      }}
    >
      {({ submitForm }) => (
        <Form translate={1}>
          <Dialog
            open={!!props.user}
            onClose={props.onClose}
            classes={{ paper: classes.paper }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">{isNew ? 'Add new user' : 'Edit user'}</DialogTitle>
            <DialogContent style={{ overflow: 'visible' }}>
              <DialogContentText id="alert-dialog-description">
                Please supply the user details below. New users will receive an invite email with a link so they can set
                their own password.
              </DialogContentText>

              <Field component={TextField} name="email" type="email" label="Email" fullWidth margin="normal" />
              <br />
              <Field component={TextField} type="text" label="Firstname" name="firstname" fullWidth margin="normal" />
              <br />
              <Field component={TextField} type="text" label="Lastname" name="lastname" fullWidth margin="normal" />
              <br />
              <SelectRole roles={roles} onrolesChange={onrolesChange} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => props.onClose(false)} color="secondary">
                Cancel
              </Button>
              <Button onClick={submitForm} color="primary">
                {isNew ? 'Add' : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>
        </Form>
      )}
    </Formik>
  );
};

export default withRouter(CreateEditUser);
